import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Gift, Shield, DollarSign, Users, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';

const tg = () => window.Telegram?.WebApp;

function HostInfo() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen pb-6">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-slate-700/50 backdrop-blur-lg bg-slate-900/80">
        <div className="px-4 py-3 flex items-center gap-3">
          <button data-testid="back-button" onClick={() => navigate('/')} className="text-slate-400 hover:text-white p-1">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-base font-bold text-white leading-tight">Host & Earn</h1>
            <p className="text-[11px] text-slate-500">List prizes, earn passive XRP</p>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="px-4 pt-6 pb-4 text-center">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500/20 to-amber-500/20 border border-orange-500/30 flex items-center justify-center mx-auto mb-3">
          <DollarSign className="w-7 h-7 text-orange-400" />
        </div>
        <h2 className="text-2xl font-black text-white">Earn While You Sleep</h2>
        <p className="text-sm text-slate-400 mt-2 max-w-[280px] mx-auto">
          List your NFTs or collectibles as prizes and earn XRP on every slot sold.
        </p>
      </section>

      {/* Cards */}
      <section className="px-4 space-y-3">
        <div className="bg-gradient-to-br from-orange-500/5 to-amber-500/5 border border-orange-500/15 rounded-2xl p-5">
          <div className="flex items-start gap-3.5">
            <div className="w-11 h-11 rounded-xl bg-orange-500/15 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <p className="font-bold text-white text-base">You Keep the Pool of Slot Sales</p>
              <p className="text-[12px] text-slate-400 mt-1.5 leading-relaxed">
                List your NFTs or collectibles as prizes — free to list, zero buyer fees. When the draw resolves, the full pool of entry fees (minus the 5.89% platform fee) is yours. NFT rooms pay out instantly; physical prize proceeds release from escrow after confirmed delivery.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500/5 to-indigo-500/5 border border-purple-500/15 rounded-2xl p-5">
          <div className="flex items-start gap-3.5">
            <div className="w-11 h-11 rounded-xl bg-purple-500/15 flex items-center justify-center flex-shrink-0">
              <Gift className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="font-bold text-white text-base">Full Room = 94.11% of Entry Fees</p>
              <p className="text-[12px] text-slate-400 mt-1.5 leading-relaxed">
                10 slots sold at your entry price — you pocket everything except the 5.89% platform cut. Set your prize, set the entry price, and let the players compete for your item. Buy It Now sales pay the same way: full BIN price minus 5.89%.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-emerald-500/5 to-teal-500/5 border border-emerald-500/15 rounded-2xl p-5">
          <div className="flex items-start gap-3.5">
            <div className="w-11 h-11 rounded-xl bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="font-bold text-white text-base">Escrow-Protected Physical Prizes</p>
              <p className="text-[12px] text-slate-400 mt-1.5 leading-relaxed">
                Physical items are locked in escrow. Ship with signature-required delivery — this is mandatory; items shipped without a signature requirement make the sale final, with no non-delivery protection. Winner confirms receipt, then funds are released to you. Disputes resolved by admin. Auto-releases after 14 days if winner doesn't respond.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-cyan-500/5 to-blue-500/5 border border-cyan-500/15 rounded-2xl p-5">
          <div className="flex items-start gap-3.5">
            <div className="w-11 h-11 rounded-xl bg-cyan-500/15 flex items-center justify-center flex-shrink-0">
              <Zap className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <p className="font-bold text-white text-base">NFT Prizes — Instant Verification</p>
              <p className="text-[12px] text-slate-400 mt-1.5 leading-relaxed">
                NFT ownership is verified on the XRP Ledger when you list. When a winner is declared, transfer the NFT to their wallet. Our system auto-verifies on-chain — no manual confirmation needed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Example Earnings */}
      <section className="px-4 pt-5">
        <div className="bg-slate-800/40 border border-slate-700/40 rounded-2xl p-4">
          <p className="text-xs font-bold text-white mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-indigo-400" /> Example Earnings
          </p>
          <div className="space-y-2.5">
            <div className="flex justify-between items-center bg-slate-900/40 rounded-lg px-3 py-2.5">
              <div>
                <p className="text-xs text-white font-medium">5 XRP entry room</p>
                <p className="text-[10px] text-slate-500">If all 10 slots fill</p>
              </div>
              <span className="text-sm font-bold text-emerald-400">+47.06 XRP</span>
            </div>
            <div className="flex justify-between items-center bg-slate-900/40 rounded-lg px-3 py-2.5">
              <div>
                <p className="text-xs text-white font-medium">25 XRP entry room</p>
                <p className="text-[10px] text-slate-500">If all 10 slots fill</p>
              </div>
              <span className="text-sm font-bold text-emerald-400">+235.28 XRP</span>
            </div>
            <div className="flex justify-between items-center bg-slate-900/40 rounded-lg px-3 py-2.5">
              <div>
                <p className="text-xs text-white font-medium">100 XRP entry room</p>
                <p className="text-[10px] text-slate-500">If all 10 slots fill</p>
              </div>
              <span className="text-sm font-bold text-emerald-400">+941.10 XRP</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 pt-5">
        <Button
          data-testid="host-cta"
          onClick={() => navigate('/')}
          className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 h-12 text-base font-bold rounded-xl"
        >
          Start Hosting
        </Button>
        <p className="text-[10px] text-slate-600 text-center mt-2">
          Platform fee: 5.89% of the pool. No host fee, no buyer fees, no upfront cost to list.
        </p>
      </section>
    </div>
  );
}

export default HostInfo;
